@import url('https://fonts.googleapis.com/css2?family=Archivo:wght@500;600;700;800;900&family=Inter+Tight:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500;600;700&display=swap');
@tailwind base;
@tailwind components;
@tailwind utilities;

:root {
  /* paper */
  --background-50: 0.985 0.004 92;
  --background-100: 0.965 0.006 92;
  --background-200: 0.93 0.008 90;
  --background-300: 0.885 0.01 90;
  --background-400: 0.8 0.011 88;
  --background-500: 0.7 0.012 88;
  --background-600: 0.58 0.012 88;
  --background-700: 0.46 0.012 88;
  --background-800: 0.34 0.012 88;
  --background-900: 0.24 0.012 88;
  --background-950: 0.15 0.012 88;

  /* ink */
  --foreground-50: 0.95 0.005 92;
  --foreground-100: 0.9 0.007 92;
  --foreground-200: 0.82 0.009 92;
  --foreground-300: 0.7 0.011 92;
  --foreground-400: 0.56 0.013 92;
  --foreground-500: 0.44 0.014 92;
  --foreground-600: 0.35 0.015 93;
  --foreground-700: 0.28 0.016 93;
  --foreground-800: 0.21 0.017 93;
  --foreground-900: 0.16 0.017 93;
  --foreground-950: 0.11 0.016 93;

  /* acid lime */
  --primary-50: 0.97 0.05 126;
  --primary-100: 0.94 0.09 126;
  --primary-200: 0.9 0.14 127;
  --primary-300: 0.86 0.18 127;
  --primary-400: 0.83 0.2 128;
  --primary-500: 0.8 0.2 128;
  --primary-600: 0.7 0.18 128;
  --primary-700: 0.58 0.15 128;
  --primary-800: 0.46 0.12 128;
  --primary-900: 0.36 0.09 128;
  --primary-950: 0.24 0.06 128;

  /* vermillion */
  --accent-50: 0.96 0.03 42;
  --accent-100: 0.93 0.06 41;
  --accent-200: 0.88 0.1 39;
  --accent-300: 0.8 0.16 37;
  --accent-400: 0.7 0.2 36;
  --accent-500: 0.62 0.22 35;
  --accent-600: 0.54 0.2 34;
  --accent-700: 0.45 0.17 33;
  --accent-800: 0.37 0.14 33;
  --accent-900: 0.3 0.11 33;
  --accent-950: 0.2 0.08 33;

  /* deep pine */
  --secondary-50: 0.96 0.02 165;
  --secondary-100: 0.92 0.04 164;
  --secondary-200: 0.85 0.06 163;
  --secondary-300: 0.75 0.07 162;
  --secondary-400: 0.62 0.08 161;
  --secondary-500: 0.5 0.08 160;
  --secondary-600: 0.42 0.07 160;
  --secondary-700: 0.34 0.06 160;
  --secondary-800: 0.27 0.05 161;
  --secondary-900: 0.21 0.04 162;
  --secondary-950: 0.15 0.03 163;

  --font-heading: 'Archivo', ui-sans-serif, system-ui, sans-serif;
  --font-body: 'Inter Tight', ui-sans-serif, system-ui, sans-serif;
  --font-label: 'JetBrains Mono', ui-monospace, monospace;
}

.dark {
  --background-50: 0.14 0.01 92;
  --background-100: 0.19 0.012 92;
  --background-200: 0.25 0.014 92;
  --background-300: 0.33 0.014 92;
  --background-400: 0.45 0.013 92;
  --background-500: 0.58 0.012 92;
  --background-600: 0.7 0.011 92;
  --background-700: 0.8 0.01 92;
  --background-800: 0.88 0.009 92;
  --background-900: 0.94 0.007 92;
  --background-950: 0.98 0.005 92;

  --foreground-50: 0.16 0.01 93;
  --foreground-100: 0.22 0.012 93;
  --foreground-200: 0.3 0.014 93;
  --foreground-300: 0.42 0.015 93;
  --foreground-400: 0.55 0.014 93;
  --foreground-500: 0.68 0.012 93;
  --foreground-600: 0.78 0.011 93;
  --foreground-700: 0.86 0.009 93;
  --foreground-800: 0.91 0.008 93;
  --foreground-900: 0.95 0.006 93;
  --foreground-950: 0.99 0.004 93;
}

html {
  scroll-behavior: smooth;
}

body {
  font-family: var(--font-body);
  background-color: oklch(var(--background-50));
  color: oklch(var(--foreground-950));
  -webkit-font-smoothing: antialiased;
}

h1,
h2,
h3,
h4 {
  font-family: var(--font-heading);
}

::selection {
  background-color: oklch(var(--primary-500));
  color: oklch(var(--foreground-950));
}

/* Unified keyboard focus ring.
   :focus-visible only triggers for keyboard (Tab) navigation, so mouse users
   see no change, while keyboard users get a clear box on every interactive
   element. It is drawn as a two-tone ring (paper inner + ink outer) so it stays
   visible on any surface, whether the paper background, a lime CTA, or the dark
   ink panels. */
:where(a, button, input, textarea, select, summary, [role='button'], [role='link'], [tabindex]:not([tabindex='-1'])):focus-visible {
  outline: 2px solid oklch(var(--background-50));
  outline-offset: 0;
  box-shadow: 0 0 0 4px oklch(var(--foreground-950));
}

.font-label {
  font-family: var(--font-label);
}

/* faint blueprint grid used behind ink panels */
.grid-pattern {
  background-image:
    linear-gradient(to right, oklch(var(--foreground-950) / 0.06) 1px, transparent 1px),
    linear-gradient(to bottom, oklch(var(--foreground-950) / 0.06) 1px, transparent 1px);
  background-size: 40px 40px;
}

.grid-pattern-light {
  background-image:
    linear-gradient(to right, oklch(var(--background-50) / 0.09) 1px, transparent 1px),
    linear-gradient(to bottom, oklch(var(--background-50) / 0.09) 1px, transparent 1px);
  background-size: 40px 40px;
}

/* outlined display text (stroke follows currentColor, fill is knocked out) */
.text-stroke {
  -webkit-text-stroke: 1.5px currentColor;
  -webkit-text-fill-color: transparent;
}

@keyframes marquee {
  from {
    transform: translateX(0);
  }
  to {
    transform: translateX(-50%);
  }
}

.animate-marquee {
  animation: marquee 34s linear infinite;
}

@keyframes rise {
  from {
    opacity: 0;
    transform: translateY(18px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.animate-rise {
  animation: rise 0.7s cubic-bezier(0.16, 1, 0.3, 1) both;
}

@keyframes tourbar {
  from {
    transform: scaleX(0);
  }
  to {
    transform: scaleX(1);
  }
}

.tourbar {
  transform-origin: left center;
}

@keyframes captionIn {
  from {
    opacity: 0;
    transform: translateY(7px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.caption-in {
  animation: captionIn 0.5s cubic-bezier(0.16, 1, 0.3, 1) both;
}

/* journey pipeline: the arrow springs forward in a short beat, then rests for
   the remainder of the cycle. The rest is what makes the motion read as a
   discrete signal rather than a smooth ripple. */
@keyframes flowNudge {
  0% {
    transform: translateX(0);
    opacity: 0.45;
  }
  10% {
    transform: translateX(3px);
    opacity: 1;
  }
  24% {
    transform: translateX(0);
    opacity: 0.9;
  }
  100% {
    transform: translateX(0);
    opacity: 0.45;
  }
}

.animate-flow-nudge {
  animation: flowNudge 2.4s ease-out infinite;
}

/* journey pipeline: a ring snaps outward on the same beat, then stays invisible
   until the next pulse */
@keyframes flowRing {
  0% {
    transform: scale(0.7);
    opacity: 0;
  }
  6% {
    opacity: 0.7;
  }
  36%,
  100% {
    transform: scale(1.9);
    opacity: 0;
  }
}

.animate-flow-ring {
  animation: flowRing 2.4s ease-out infinite;
}

/* Respect users who ask for less motion: collapse every animation and
   transition to its end state instead of playing it. */
@media (prefers-reduced-motion: reduce) {
  html {
    scroll-behavior: auto;
  }

  *,
  *::before,
  *::after {
    animation-duration: 0.001ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.001ms !important;
    scroll-behavior: auto !important;
  }

  /* the looping ticker tape should simply sit still, not drift to its end frame */
  .animate-marquee {
    animation: none !important;
  }
}